import { useCallback, useEffect, useRef, useState } from "react";
import type { Vector3Tuple } from "three";
import { BoxGeometry, CircleGeometry, Mesh, MeshBasicMaterial, Vector3 } from "three";

import { PROP_DEFINITIONS } from "@/components/engine/dioramaLayout";

/** Prop IDs that the lobster can walk over (no obstacle). */
const WALKABLE_PROPS = new Set(["sleepPatch"]);

export function useNavMesh() {
  const [isReady, setIsReady] = useState(false);
  const navMeshRef = useRef<unknown>(null);
  const queryRef = useRef<unknown>(null);

  useEffect(() => {
    let disposed = false;

    async function init() {
      try {
        const { init: initRecast } = await import(
          "@recast-navigation/core"
        );
        await initRecast();

        if (disposed) return;

        const { threeToSoloNavMesh } = await import(
          "@recast-navigation/three"
        );

        const geometry = new CircleGeometry(7.5, 8);
        geometry.rotateX(-Math.PI / 2);
        const floorMesh = new Mesh(geometry, new MeshBasicMaterial());

        // Add obstacle boxes for office props (except walkable ones)
        const obstacleMeshes: Mesh[] = [];
        for (const prop of PROP_DEFINITIONS) {
          if (WALKABLE_PROPS.has(prop.id)) continue;
          const obsGeo = new BoxGeometry(1.8, 2, 1.8);
          const obsMesh = new Mesh(obsGeo, new MeshBasicMaterial());
          obsMesh.position.set(prop.position[0], 1, prop.position[2]);
          obsMesh.updateMatrixWorld(true);
          obstacleMeshes.push(obsMesh);
        }

        const { success, navMesh } = threeToSoloNavMesh(
          [floorMesh, ...obstacleMeshes],
          {
            cs: 0.3,
            ch: 0.2,
            walkableRadius: 1,
          },
        );

        geometry.dispose();
        floorMesh.material.dispose();
        for (const m of obstacleMeshes) {
          m.geometry.dispose();
          (m.material as MeshBasicMaterial).dispose();
        }

        if (!success || !navMesh || disposed) return;

        const { NavMeshQuery } = await import("@recast-navigation/core");
        const query = new NavMeshQuery(navMesh);

        navMeshRef.current = navMesh;
        queryRef.current = query;
        setIsReady(true);
      } catch (err) {
        console.error("Nav mesh initialization failed:", err);
        setIsReady(false);
      }
    }

    void init();

    return () => {
      disposed = true;
    };
  }, []);

  const computePath = useCallback(
    (from: Vector3Tuple, to: Vector3Tuple): Vector3Tuple[] => {
      if (!isReady || !queryRef.current) {
        return [to];
      }

      try {
        const query = queryRef.current as {
          computePath: (
            start: Vector3,
            end: Vector3,
          ) => { success: boolean; path: Vector3[] };
        };
        const result = query.computePath(
          new Vector3(...from),
          new Vector3(...to),
        );

        if (result.success && result.path.length > 0) {
          return result.path.map(
            (p) => [p.x, p.y, p.z] as Vector3Tuple,
          );
        }
      } catch (err) {
        console.warn("Pathfinding failed, using direct path:", err);
      }

      return [to];
    },
    [isReady],
  );

  return { computePath, isReady };
}
